import { useEffect, useState, useCallback, useRef } from "react";
import {
  Video,
  Mic,
  Users,
  Headphones,
  Activity,
  Square,
  SwitchCamera,
  Moon,
  Power,
  Volume2,
  Eye,
  Shield,
  Wifi,
  WifiOff,
} from "lucide-react";

import {
  useMediaDevices,
  useWebRTC,
  useMotionDetection,
  useDeviceStatus,
  useBackgroundActivity,
} from "@/hooks";
import {
  TIMING,
  STORAGE_KEYS,
  MOTION_SENSITIVITY_LEVELS,
  type MotionSensitivityLevel,
  LULLABIES,
  LULLABY_FADE_DURATION_MS,
} from "@/constants";
import {
  ActionCard,
  IconButton,
  InlineMessage,
  ModeHero,
  MonitorButton,
  StatusLine,
} from "@/components/monitor/MonitorUI";
import styles from "./BabyModePage.module.scss";

// ============================================================
// Helper Functions
// ============================================================

function loadLastMotionTimestamp(): number | null {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem(STORAGE_KEYS.lastMotionTimestamp);
  return stored ? parseInt(stored, 10) : null;
}

function saveLastMotionTimestamp(timestamp: number): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(STORAGE_KEYS.lastMotionTimestamp, timestamp.toString());
}

function BabyMonitorIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 19a10 10 0 0 0 16 0" />
      <path d="M4 14h16" />
      <path d="M6 14V9a6 6 0 0 1 12 0v5" />
      <circle cx="12" cy="9" r="1.5" fill="currentColor" />
    </svg>
  );
}

// ============================================================
// Component
// ============================================================

export function BabyModePage() {
  // ============================================================
  // Media Devices Hook
  // ============================================================

  const {
    stream,
    videoRef,
    cameraStatus,
    micStatus,
    error: mediaError,
    audioLevel,
    isVideoEnabled,
    startVideoAndAudio,
    startAudioOnly,
    stopAll,
    videoDevices,
    switchCamera,
  } = useMediaDevices();

  // Ref for playing parent's audio (used in callbacks below)
  const parentAudioRef = useRef<HTMLAudioElement>(null);

  // Ref for lullaby audio player (stays in DOM, set-and-forget)
  const lullabyAudioRef = useRef<HTMLAudioElement>(null);
  const lullabyFadeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );

  // ============================================================
  // Parent Audio Callbacks (defined before useWebRTC)
  // ============================================================

  const handleParentAudioTrack = useCallback(
    (_track: MediaStreamTrack, stream: MediaStream) => {
      console.log("[BabyMode] Received parent audio track");
      if (parentAudioRef.current) {
        parentAudioRef.current.srcObject = stream;
        parentAudioRef.current
          .play()
          .then(() => {
            console.log("[BabyMode] Playing parent audio");
          })
          .catch((err) => {
            console.warn("[BabyMode] Failed to play parent audio:", err);
          });
      }
    },
    [],
  );

  const handleParentTalkState = useCallback(
    (talking: boolean, parentId: string) => {
      console.log(
        `[BabyMode] Parent ${parentId} ${talking ? "started" : "stopped"} talking`,
      );
      setIsParentTalking(talking);

      if (!talking && parentAudioRef.current) {
        parentAudioRef.current.pause();
        parentAudioRef.current.srcObject = null;
      }
    },
    [],
  );

  // ============================================================
  // WebRTC Hook
  // ============================================================

  const {
    connectionState,
    connectedPeersCount,
    error: connectionError,
    disconnect,
    signalingClient,
  } = useWebRTC({
    role: "baby",
    localStream: stream,
    onParentAudioTrack: handleParentAudioTrack,
    onParentTalkState: handleParentTalkState,
  });

  // ============================================================
  // State
  // ============================================================

  const [parentCount, setParentCount] = useState(0);
  const [showMotionAlert, setShowMotionAlert] = useState(false);
  const [lastMotionTimestamp, setLastMotionTimestamp] = useState<number | null>(
    () => loadLastMotionTimestamp(),
  );
  const [isBlindsActive, setIsBlindsActive] = useState(false);
  const blindsOverlayRef = useRef<HTMLButtonElement>(null);
  const darkModeButtonRef = useRef<HTMLButtonElement>(null);
  const [isParentTalking, setIsParentTalking] = useState(false);
  const [currentLullabyId, setCurrentLullabyId] = useState<string | null>(null);
  const [isLullabyPlaying, setIsLullabyPlaying] = useState(false);

  // Suppress unused variable warnings while feature is disabled
  void isParentTalking;
  void Volume2;

  // ============================================================
  // Derived State
  // ============================================================

  const watchingCount = connectedPeersCount;
  const isStreaming = micStatus === "active";
  const isRequesting =
    cameraStatus === "requesting" || micStatus === "requesting";
  const isConnected = connectionState === "connected";
  const error = mediaError || connectionError;

  const exitBlinds = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch((err) => console.error(err));
    }
    setIsBlindsActive(false);
    requestAnimationFrame(() => darkModeButtonRef.current?.focus());
  }, []);

  useEffect(() => {
    if (!isBlindsActive) return;
    blindsOverlayRef.current?.focus();

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        event.preventDefault();
        blindsOverlayRef.current?.focus();
        return;
      }
      if (
        event.key === "Escape" ||
        event.key === "Enter" ||
        event.key === " "
      ) {
        event.preventDefault();
        exitBlinds();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isBlindsActive, exitBlinds]);

  // ============================================================
  // Motion Detection
  // ============================================================

  const handleMotionDetected = useCallback(() => {
    const timestamp = Date.now();
    console.log("[BabyMode] Motion detected!");

    setShowMotionAlert(true);
    setTimeout(() => setShowMotionAlert(false), TIMING.motionAlertDuration);

    setLastMotionTimestamp(timestamp);
    saveLastMotionTimestamp(timestamp);

    signalingClient.send({
      type: "motion-alert",
      timestamp,
    });
  }, [signalingClient]);

  const {
    isEnabled: isMotionDetectionEnabled,
    motionCount,
    deviceCapability,
    toggleEnabled: toggleMotionDetection,
    sensitivityLevel,
    setSensitivityLevel,
  } = useMotionDetection(videoRef, isVideoEnabled, {
    onMotionDetected: handleMotionDetected,
  });

  // ============================================================
  // Device Status Hook
  // ============================================================

  const { batteryLevel, isCharging, networkType } = useDeviceStatus();

  // ============================================================
  // Background Activity Hook
  // ============================================================

  useBackgroundActivity({
    enabled: isStreaming,
    showNotification: false,
    onBackground: () => {
      console.log("[BabyMode] App went to background while streaming");
    },
    onForeground: () => {
      console.log("[BabyMode] App returned to foreground");
    },
  });

  // ============================================================
  // Device State Broadcasting
  // ============================================================

  const broadcastDeviceState = useCallback(() => {
    console.log(
      `[BabyMode] Broadcasting state: video=${isVideoEnabled}, mic=${
        micStatus === "active"
      }, battery=${batteryLevel}, charging=${isCharging}, network=${networkType}`,
    );

    signalingClient.send({
      type: "device-state",
      cameraActive: isVideoEnabled,
      micActive: micStatus === "active",
      motionDetectionEnabled: isMotionDetectionEnabled,
      lastMotionDetected: lastMotionTimestamp,
      batteryLevel,
      isCharging,
      networkType,
    });
  }, [
    isVideoEnabled,
    micStatus,
    signalingClient,
    isMotionDetectionEnabled,
    lastMotionTimestamp,
    batteryLevel,
    isCharging,
    networkType,
  ]);

  useEffect(() => {
    broadcastDeviceState();
  }, [broadcastDeviceState]);

  useEffect(() => {
    const handleBroadcastStateRequest = () => {
      console.log(
        "[BabyMode] Received broadcast-state request, sending current state immediately",
      );
      broadcastDeviceState();
    };

    signalingClient.on("broadcast-state", handleBroadcastStateRequest);
    return () =>
      signalingClient.off("broadcast-state", handleBroadcastStateRequest);
  }, [signalingClient, broadcastDeviceState]);

  // ============================================================
  // Lullaby Playback Functions
  // ============================================================

  const fadeAudioIn = useCallback(
    (audio: HTMLAudioElement, targetVolume = 1) => {
      if (lullabyFadeIntervalRef.current) {
        clearInterval(lullabyFadeIntervalRef.current);
      }

      audio.volume = 0;
      const steps = 20;
      const stepDuration = LULLABY_FADE_DURATION_MS / steps;
      const volumeStep = targetVolume / steps;
      let currentStep = 0;

      lullabyFadeIntervalRef.current = setInterval(() => {
        currentStep++;
        audio.volume = Math.min(currentStep * volumeStep, targetVolume);

        if (currentStep >= steps) {
          if (lullabyFadeIntervalRef.current) {
            clearInterval(lullabyFadeIntervalRef.current);
            lullabyFadeIntervalRef.current = null;
          }
        }
      }, stepDuration);
    },
    [],
  );

  const fadeAudioOut = useCallback(
    (audio: HTMLAudioElement, onComplete?: () => void) => {
      if (lullabyFadeIntervalRef.current) {
        clearInterval(lullabyFadeIntervalRef.current);
      }

      const startVolume = audio.volume;
      const steps = 20;
      const stepDuration = LULLABY_FADE_DURATION_MS / steps;
      const volumeStep = startVolume / steps;
      let currentStep = 0;

      lullabyFadeIntervalRef.current = setInterval(() => {
        currentStep++;
        audio.volume = Math.max(startVolume - currentStep * volumeStep, 0);

        if (currentStep >= steps) {
          if (lullabyFadeIntervalRef.current) {
            clearInterval(lullabyFadeIntervalRef.current);
            lullabyFadeIntervalRef.current = null;
          }
          audio.pause();
          audio.currentTime = 0;
          onComplete?.();
        }
      }, stepDuration);
    },
    [],
  );

  const broadcastLullabyState = useCallback(
    (playing: boolean, lullabyId?: string) => {
      signalingClient.send({
        type: "lullaby-state",
        playing,
        lullabyId,
      });
    },
    [signalingClient],
  );

  const playLullaby = useCallback(
    (lullabyId: string) => {
      const lullaby = LULLABIES.find((l) => l.id === lullabyId);
      if (!lullaby || !lullabyAudioRef.current) {
        console.warn("[BabyMode] Lullaby not found:", lullabyId);
        return;
      }

      console.log("[BabyMode] Playing lullaby:", lullaby.name);

      const audio = lullabyAudioRef.current;
      audio.src = lullaby.url;
      audio.loop = true;

      audio
        .play()
        .then(() => {
          fadeAudioIn(audio, 0.7);
          setCurrentLullabyId(lullabyId);
          setIsLullabyPlaying(true);
          broadcastLullabyState(true, lullabyId);
        })
        .catch((err) => {
          console.warn("[BabyMode] Failed to play lullaby:", err);
          broadcastLullabyState(false);
        });
    },
    [fadeAudioIn, broadcastLullabyState],
  );

  const stopLullaby = useCallback(() => {
    if (!lullabyAudioRef.current || !isLullabyPlaying) return;

    console.log("[BabyMode] Stopping lullaby");

    fadeAudioOut(lullabyAudioRef.current, () => {
      setCurrentLullabyId(null);
      setIsLullabyPlaying(false);
      broadcastLullabyState(false);
    });
  }, [isLullabyPlaying, fadeAudioOut, broadcastLullabyState]);

  useEffect(() => {
    const handleLullabyControl = (data: any) => {
      console.log("[BabyMode] Lullaby control received:", data);

      if (!isStreaming) {
        console.log("[BabyMode] Not streaming, ignoring lullaby control");
        return;
      }

      if (data.action === "play" && data.lullabyId) {
        if (isLullabyPlaying && currentLullabyId !== data.lullabyId) {
          if (lullabyAudioRef.current) {
            lullabyAudioRef.current.pause();
            lullabyAudioRef.current.currentTime = 0;
          }
        }
        playLullaby(data.lullabyId);
      } else if (data.action === "stop") {
        stopLullaby();
      }
    };

    signalingClient.on("lullaby-control", handleLullabyControl);
    return () => signalingClient.off("lullaby-control", handleLullabyControl);
  }, [
    signalingClient,
    isStreaming,
    isLullabyPlaying,
    currentLullabyId,
    playLullaby,
    stopLullaby,
  ]);

  useEffect(() => {
    if (!isStreaming && isLullabyPlaying) {
      console.log("[BabyMode] Streaming stopped, stopping lullaby");
      stopLullaby();
    }
  }, [isStreaming, isLullabyPlaying, stopLullaby]);

  useEffect(() => {
    return () => {
      if (lullabyFadeIntervalRef.current) {
        clearInterval(lullabyFadeIntervalRef.current);
      }
    };
  }, []);

  // ============================================================
  // Event Handlers
  // ============================================================

  useEffect(() => {
    const handleDeviceList = (data: any) => {
      const devices = data.devices || [];
      const parents = devices.filter((d: any) => d.role === "parent");
      setParentCount(parents.length);
    };

    signalingClient.on("device-list", handleDeviceList);

    const pingIntervalMs = isStreaming ? 1500 : 3000;
    const pingInterval = setInterval(() => {
      if (signalingClient.isConnected()) {
        signalingClient.send({ type: "ping" });
      }
    }, pingIntervalMs);

    return () => {
      signalingClient.off("device-list", handleDeviceList);
      clearInterval(pingInterval);
    };
  }, [signalingClient, isStreaming]);

  // ============================================================
  // Action Handlers
  // ============================================================

  const handleStop = () => {
    console.log("[BabyMode] Stopping streaming");
    if (isConnected) {
      disconnect();
    }
    stopAll();
  };

  // ============================================================
  // Render
  // ============================================================

  return (
    <div className={styles.page}>
      {/* Screen Blinds Overlay - Darkens screen for sleeping baby */}
      {isBlindsActive && (
        <button
          type="button"
          ref={blindsOverlayRef}
          className={styles.blindsOverlay}
          aria-label="Exit dark room mode"
          onClick={exitBlinds}
        >
          <span className={styles.blindsHint}>
            Tap anywhere to exit dark mode
          </span>
        </button>
      )}

      {/* Hidden audio elements */}
      <audio ref={lullabyAudioRef} style={{ display: "none" }} />

      <div className={styles.container} inert={isBlindsActive}>
        {/* ============================================ */}
        {/* IDLE STATE — Not Streaming                  */}
        {/* ============================================ */}
        {!isStreaming && (
          <div className={styles.idleScreen}>
            {/* Hero area */}
            <div className={styles.idleHero}>
              <ModeHero
                icon={<BabyMonitorIcon />}
                status="Ready"
                title="Baby Mode"
                description="Place this device facing baby"
              />
            </div>

            {/* Bottom Section */}
            <div className={styles.idleBottom}>
              {/* Error */}
              {error && <InlineMessage>{error}</InlineMessage>}

              {/* Parent status — left-aligned, above buttons */}
              <StatusLine tone={parentCount > 0 ? "success" : "neutral"}>
                {parentCount > 0
                  ? `${parentCount} parent${parentCount !== 1 ? "s" : ""} online`
                  : "No parents online"}
              </StatusLine>

              {/* Start buttons */}
              <div className={styles.startButtons}>
                <ActionCard
                  onClick={startVideoAndAudio}
                  disabled={isRequesting}
                  icon={<Video size={22} strokeWidth={2.2} />}
                  label="Start Video + Audio"
                  loading={cameraStatus === "requesting"}
                  loadingLabel="Requesting permission…"
                />

                <ActionCard
                  onClick={startAudioOnly}
                  disabled={isRequesting}
                  icon={<Mic size={22} strokeWidth={2.2} />}
                  label="Start Audio Only"
                  tone="dark"
                  loading={
                    micStatus === "requesting" && cameraStatus !== "requesting"
                  }
                  loadingLabel="Requesting permission…"
                />
              </div>

              {/* Security note */}
              <div className={styles.securityNote}>
                <Shield size={14} />
                <span>End-to-end encrypted connection</span>
              </div>
            </div>
          </div>
        )}

        {/* ============================================ */}
        {/* STREAMING STATE                             */}
        {/* ============================================ */}
        {isStreaming && (
          <div className={styles.streamingScreen}>
            {/* Top bar */}
            <div className={styles.streamTopBar}>
              <div className={styles.liveBadge}>
                <div className={styles.liveDot} />
                <span>{isVideoEnabled ? "LIVE" : "AUDIO"}</span>
              </div>

              <div className={styles.streamTopRight}>
                {watchingCount > 0 && (
                  <div className={styles.viewersBadge}>
                    <Eye size={14} />
                    <span>{watchingCount} watching</span>
                  </div>
                )}
                <div
                  className={`${styles.parentsBadge} ${parentCount > 0 ? styles.online : ""}`}
                >
                  <Users size={14} />
                  <span>
                    {parentCount} {parentCount === 1 ? "parent" : "parents"}
                  </span>
                </div>
              </div>
            </div>

            {/* Video / Audio area */}
            <div
              className={`${styles.mediaArea} ${
                isVideoEnabled ? styles.videoMode : styles.audioMode
              }`}
            >
              {/* Video element (always in DOM, hidden when audio-only) */}
              <video
                ref={videoRef}
                className={styles.video}
                autoPlay
                playsInline
                muted
              />

              {/* Audio-only visual */}
              {!isVideoEnabled && (
                <div className={styles.audioOnlyVisual}>
                  <div className={styles.audioRadar} aria-hidden="true">
                    <div
                      className={styles.audioWave}
                      style={{
                        transform: `scale(${1 + Math.min(100, Math.max(0, audioLevel)) / 500})`,
                      }}
                    />
                    <Headphones size={36} strokeWidth={1.8} />
                  </div>
                  <span className={styles.audioOnlyLabel}>Audio Only</span>
                </div>
              )}

              {/* Audio level bar (video mode) */}
              {isVideoEnabled && (
                <div className={styles.audioBar}>
                  <div className={styles.audioBarTrack}>
                    <div
                      className={styles.audioBarFill}
                      style={{ height: `${audioLevel}%` }}
                    />
                  </div>
                  <Mic size={12} />
                </div>
              )}

              {/* Dark room mode button */}
              <IconButton
                ref={darkModeButtonRef}
                className={styles.darkModeBtn}
                onClick={() => {
                  if (!document.fullscreenElement) {
                    document.documentElement
                      .requestFullscreen()
                      .catch((err) => {
                        console.error(
                          `Error attempting to enable fullscreen: ${err.message} (${err.name})`,
                        );
                      });
                  }
                  setIsBlindsActive(true);
                }}
                title="Dark Room Mode"
                aria-label="Enter dark room mode"
              >
                <Moon size={18} />
              </IconButton>

              {/* Switch camera (if available) */}
              {isVideoEnabled && videoDevices.length > 1 && (
                <IconButton
                  className={styles.switchCameraBtn}
                  onClick={switchCamera}
                  title="Switch Camera"
                  aria-label="Switch camera"
                >
                  <SwitchCamera size={18} />
                </IconButton>
              )}
            </div>

            {/* Stream info strip */}
            <div className={styles.streamInfo}>
              <div className={styles.streamInfoLeft}>
                <div className={styles.streamBadge}>
                  {isVideoEnabled ? (
                    <Video size={14} />
                  ) : (
                    <Headphones size={14} />
                  )}
                  <span>{isVideoEnabled ? "Video" : "Audio"}</span>
                </div>
                <div className={styles.streamBadge}>
                  <Mic size={14} />
                  <span>Mic</span>
                </div>
                {networkType && (
                  <div className={styles.streamBadge}>
                    {networkType === "offline" ? (
                      <WifiOff size={14} />
                    ) : (
                      <Wifi size={14} />
                    )}
                    <span>{networkType}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Motion Detection */}
            {isVideoEnabled && (
              <div
                className={`${styles.motionCard} ${isMotionDetectionEnabled ? styles.active : ""}`}
              >
                <div className={styles.motionRow}>
                  <div
                    className={`${styles.motionIcon} ${
                      isMotionDetectionEnabled ? styles.active : ""
                    } ${showMotionAlert ? styles.pulse : ""}`}
                  >
                    <Activity size={18} />
                  </div>
                  <div className={styles.motionInfo}>
                    <span className={styles.motionTitle}>Motion Detection</span>
                    <span className={styles.motionSub}>
                      {isMotionDetectionEnabled
                        ? `Active${motionCount > 0 ? ` · ${motionCount} detected` : ""}`
                        : "Off"}
                    </span>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isMotionDetectionEnabled}
                    aria-label="Motion detection"
                    className={`${styles.motionToggle} ${
                      isMotionDetectionEnabled ? styles.on : ""
                    }`}
                    onClick={toggleMotionDetection}
                    title={
                      deviceCapability
                        ? `Motion Detection (${deviceCapability.tier} tier device)`
                        : "Detecting device capability..."
                    }
                  >
                    <Power size={16} />
                  </button>
                </div>

                {/* Sensitivity pills */}
                {isMotionDetectionEnabled && (
                  <div className={styles.sensitivityRow}>
                    <div
                      className={styles.sensitivityPills}
                      role="group"
                      aria-label="Motion sensitivity"
                    >
                      {(
                        Object.keys(
                          MOTION_SENSITIVITY_LEVELS,
                        ) as MotionSensitivityLevel[]
                      ).map((level) => (
                        <button
                          type="button"
                          key={level}
                          aria-pressed={sensitivityLevel === level}
                          className={`${styles.pill} ${
                            sensitivityLevel === level ? styles.active : ""
                          }`}
                          onClick={() => setSensitivityLevel(level)}
                        >
                          {MOTION_SENSITIVITY_LEVELS[level].label}
                        </button>
                      ))}
                    </div>
                    <span className={styles.sensitivityDesc}>
                      {MOTION_SENSITIVITY_LEVELS[sensitivityLevel].description}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Stop button */}
            <MonitorButton
              className={styles.stopBtn}
              variant="destructive"
              onClick={handleStop}
            >
              <span className={styles.stopLabel}>
                <Square size={18} strokeWidth={2.5} fill="currentColor" />
                Stop Streaming
              </span>
            </MonitorButton>
          </div>
        )}
      </div>
    </div>
  );
}
